import { useEffect } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { useSelectIsL2Visible } from 'Deer/slice/DeerNav.slice';
import { useDispatch } from 'react-redux';
import {
  REVIEW_FORM_STAGE,
  setReviewCreationKey,
  useReviewCreationV1Data,
  resetReviewCreation,
} from 'reducers/reviewCreationV1Slice';

import { ArrowRight } from 'assets/assetsV1/iconComponents';
import { Launch } from 'assets/assetsV1/iconComponents/Launch';

import { Button } from 'uiToolkitV1/Button';
import { Checkbox } from 'uiToolkitV1/Checkbox';
import { ConfirmationModal } from 'uiToolkitV1/ConfirmationModal';
import { PaperWithShadow } from 'uiToolkitV1/Paper';
import { TextBodyMedium, TextHeader4, TextLabel } from 'uiToolkitV1/Typography';

import { ReviewProcessV2Footer } from './ReviewProcessV2Footer';
import { RestrictAccessContainer } from './components/SectionWrapper';
import { FormsSettingsPanel } from './configurePanels/FormsSettingsPanel';
import { TimelinesRemindersPanel } from './configurePanels/TimelinesRemindersPanel';
import { ValidationPanel } from './configurePanels/ValidationPanel';
import { CalibrationAndDeliverablesPanel } from './configurePanels/calibrationAndDeliverables/CalibrationAndDeliverablesPanel';
import { useGetReviewsCalibrationAndDeliverables } from './dataHooks/calibrationAndDeliverablesSectionHooks';
import { useGetReviewForms } from './dataHooks/formsSectionHooks';
import { useGetReviewCycleMetadata } from './dataHooks/nameAndRevieweesSectionHooks';
import {
  useGetTimelineReminders,
  useLaunchReviewCycle,
  useUpdateLaunchMailPreference,
} from './dataHooks/timelineAndRemindersSectionHooks';
import useReviewCreationFormId from './hooks/useReviewCreationFormId';
import { CalibrationAndDeliverables } from './sections/CalibrationAndDeliverables';
import { Forms } from './sections/Forms';
import { NameAndReviewees, REVIEW_CREATION_TYPES } from './sections/NameAndReviewees';
import { TimelinesRemindersAndLaunch } from './sections/TimelineRemindersAndLaunch';

const ConfirmationModalComp = () => {
  const { isConfirmationModalOpen } = useReviewCreationV1Data();
  const { mutate: lauchCycle, isLoading } = useLaunchReviewCycle();

  const dispatch = useDispatch();

  const handleCancelClick = () => {
    dispatch(setReviewCreationKey({ isConfirmationModalOpen: false }));
  };

  return (
    <ConfirmationModal
      isCentered
      isOpen={isConfirmationModalOpen}
      title={
        <Flex alignItems="center" gap={2}>
          <Launch boxSize="24px" color="icon.g2" />
          <TextHeader4 bold>Launch review cycle</TextHeader4>
        </Flex>
      }
      body={
        <Flex flexDirection="column">
          <TextBodyMedium>Are you sure you want to launch this review cycle?</TextBodyMedium>
          <TextLabel color="text.g2">
            Please review all changes carefully before launching the review cycle, as changes cannot be made
            post-launch.
          </TextLabel>
        </Flex>
      }
      showTopCloseButton={false}
      cancelText="Cancel"
      onCancel={handleCancelClick}
      continueText={isLoading ? 'Launching Review Cycle...' : 'Launch'}
      disabled={isLoading}
      onContinue={lauchCycle}
    />
  );
};

export const ReviewProcessV2 = () => {
  const reviewCycleId = useReviewCreationFormId();
  // we don't need value here as it directly updates the store
  const { isSendLaunchComm, isLaunchConfirmationChecked, reviewType, reviewStage } = useReviewCreationV1Data();
  const isDraft = reviewStage === REVIEW_FORM_STAGE.DRAFT;
  const { mutate: updateLauchMailPreference } = useUpdateLaunchMailPreference();

  useGetReviewCycleMetadata();
  useGetReviewForms({ reviewCycleId });
  useGetTimelineReminders();
  useGetReviewsCalibrationAndDeliverables();

  const dispatch = useDispatch();
  const isL2NavVisible = useSelectIsL2Visible();

  useEffect(() => {
    dispatch(setReviewCreationKey({ reviewCycleId }));
    return () => {
      dispatch(resetReviewCreation());
    };
  }, [dispatch, reviewCycleId]);

  const onMailToParticipantsChange = (e) => {
    dispatch(setReviewCreationKey({ isSendLaunchComm: e.target.checked }));
    updateLauchMailPreference({ isSendLaunchComm: e.target.checked });
  };

  const onLaunchConfirmationChange = (e) => {
    dispatch(setReviewCreationKey({ isLaunchConfirmationChecked: e.target.checked }));
  };

  const handleViewValidationClick = () => {
    dispatch(setReviewCreationKey({ isValidationPanelOpen: true }));
  };

  return (
    <PaperWithShadow
      display="flex"
      flexDir="column"
      py={0}
      flex={1}
      overflow="auto"
      // might remove
      ml={isL2NavVisible ? '0' : '34px'}
      transition="margin .3s ease;"
    >
      <Flex flex={1} p={4} overflow="auto" borderRadius="8px" bg="white" flexDir="column" gap={3}>
        <Flex flex={1} p={2} borderRadius="8px" bg="surface.F5F7F9" flexDir="column" gap={2}>
          <NameAndReviewees />

          <Forms />
          <FormsSettingsPanel />

          <CalibrationAndDeliverables />
          <CalibrationAndDeliverablesPanel />

          <TimelinesRemindersAndLaunch />
          <TimelinesRemindersPanel />

          {reviewType === REVIEW_CREATION_TYPES.DEFAULT && <ValidationPanel />}
        </Flex>
        <Box mt={1}>
          {isDraft && (
            <Flex mb={2} alignItems="center">
              <Checkbox isChecked={isLaunchConfirmationChecked} onChange={onLaunchConfirmationChange}>
                I have reviewed & understood that the data may be missing for certain employees which might impact their
                review experience.
              </Checkbox>
              {reviewType === REVIEW_CREATION_TYPES.DEFAULT && (
                <Button
                  EndIcon={ArrowRight}
                  variant="tertiary"
                  height="32px"
                  padding="0px"
                  onClick={handleViewValidationClick}
                >
                  View more
                </Button>
              )}
            </Flex>
          )}
          <RestrictAccessContainer restrictAccess={!isDraft}>
            <Checkbox isChecked={isSendLaunchComm} onChange={onMailToParticipantsChange}>
              Send the review launch mail to the participants (self reviewers) on their review start date.
            </Checkbox>
          </RestrictAccessContainer>
        </Box>
      </Flex>

      <ReviewProcessV2Footer />
      <ConfirmationModalComp />
    </PaperWithShadow>
  );
};
